import React, { useState, useEffect } from 'react';
import { Typography, Card, Row, Col, Button, Space, Table, Tag, message } from 'antd';
import {
  PlusOutlined,
  FilterOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  DatabaseOutlined,
  DollarOutlined,
  AppstoreOutlined,
  BankOutlined,
  UserOutlined,
  GlobalOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import apiClient from '../services/apiClient';
import { useAuth } from '../hooks/useAuth';
import CostingRuleDetails from '../components/CostingRuleDetails';
import COGSCreationModal from '../components/COGSCreationModal';

const { Title, Text } = Typography;

const HomePage = () => {
  const [recentRules, setRecentRules] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [draftRules, setDraftRules] = useState([]);
  const [rejectedRules, setRejectedRules] = useState([]);
  const [selectedRuleId, setSelectedRuleId] = useState(null);
  const [detailsVisible, setDetailsVisible] = useState(false);

  // COGS Creation Modal states
  const [createFormVisible, setCreateFormVisible] = useState(false);

  const navigate = useNavigate();
  const { user } = useAuth();
  const userData = user?.data;

  useEffect(() => {
    fetchDashboardData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);



  const fetchDashboardData = async () => {
    try {
      const [rulesResponse, approvalsResponse] = await Promise.all([
        apiClient.get('/api/costing-rules?limit=100'),
        apiClient.get('/api/approvals/pending')
      ]);

      const rules = rulesResponse.data.data.rules || [];
      const approvals = approvalsResponse.data.data.rules || [];



      // Set recent data
      setRecentRules(rules.slice(0, 5));
      setPendingApprovals(approvals.slice(0, 5));
      setDraftRules(rules.filter(rule => rule.status === 'DRAFT' && rule.proposerId === userData?.id).slice(0, 5));

      // Get rejected rules that user has access to (based on entity access)
      const userEntityIds = userData?.accessibleEntities?.map(e => e.id) || [];
      const rejectedRulesFiltered = rules.filter(rule =>
        rule.status === 'REJECTED' &&
        (userData?.role === 'Admin' || userEntityIds.includes(rule.entityId))
      );
      setRejectedRules(rejectedRulesFiltered.slice(0, 5));

    } catch (error) {
      message.error('Failed to load dashboard data');
      // eslint-disable-next-line no-console
      // Error is handled by the UI state
    }
  };



  const handleRuleClick = (ruleId) => {
    setSelectedRuleId(ruleId);
    setDetailsVisible(true);
  };

  // Handle COGS creation workflow
  const handleCreateCOGS = () => {
    setCreateFormVisible(true);
  };

  const handleCOGSFormSuccess = async () => {
    await fetchDashboardData(); // Refresh dashboard
  };

  const handleCOGSFormCancel = () => {
    setCreateFormVisible(false);
  };

  const getStatusColor = (status) => {
    const colors = {
      DRAFT: 'default',
      PENDING: 'processing',
      APPROVED: 'success',
      REJECTED: 'error'
    };
    return colors[status] || 'default';
  };

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>COGS Management Dashboard</Title>
        <Text type="secondary">
          Manage and track Cost of Goods Sold data across all entities
        </Text>
      </div>



      <Row gutter={[16, 16]}>
        {/* Recent Active Rules */}
        <Col xs={24} lg={12}>
          <Card
            title="Recent Active Rules"
            extra={
              <Button
                type="link"
                onClick={() => navigate('/costing-rules?status=APPROVED')}
              >
                View All
              </Button>
            }
          >
            <Table
              dataSource={recentRules.filter(rule => rule.status === 'APPROVED')}
              columns={[
                {
                  title: 'Entity',
                  dataIndex: ['entity', 'name'],
                  key: 'entity',
                  ellipsis: true,
                },
                {
                  title: 'Product',
                  dataIndex: ['product', 'name'],
                  key: 'product',
                  ellipsis: true,
                },
                {
                  title: 'Status',
                  dataIndex: 'status',
                  key: 'status',
                  render: (status) => (
                    <Tag color={getStatusColor(status)}>{status}</Tag>
                  ),
                },
                {
                  title: 'Action',
                  key: 'action',
                  render: (_, record) => (
                    <Button
                      type="link"
                      icon={<EyeOutlined />}
                      onClick={() => handleRuleClick(record.id)}
                    >
                      View
                    </Button>
                  ),
                },
              ]}
              pagination={false}
              size="small"
              rowKey="id"
            />
          </Card>
        </Col>

        {/* Pending Approvals */}
        <Col xs={24} lg={12}>
          <Card
            title="Pending Approvals"
            extra={
              <Button
                type="link"
                onClick={() => navigate('/approvals')}
              >
                View All
              </Button>
            }
          >
            <Table
              dataSource={pendingApprovals}
              columns={[
                {
                  title: 'Entity',
                  dataIndex: ['entity', 'name'],
                  key: 'entity',
                  ellipsis: true,
                },
                {
                  title: 'Product',
                  dataIndex: ['product', 'name'],
                  key: 'product',
                  ellipsis: true,
                },
                {
                  title: 'Proposer',
                  dataIndex: ['proposer', 'name'],
                  key: 'proposer',
                  ellipsis: true,
                },
                {
                  title: 'Action',
                  key: 'action',
                  render: (_, record) => (
                    <Button
                      type="link"
                      icon={<EyeOutlined />}
                      onClick={() => handleRuleClick(record.id)}
                    >
                      Review
                    </Button>
                  ),
                },
              ]}
              pagination={false}
              size="small"
              rowKey="id"
            />
          </Card>
        </Col>

        {/* Draft Rules */}
        <Col xs={24} lg={12}>
          <Card
            title="My Draft Rules"
            extra={
              <Button
                type="link"
                onClick={() => navigate('/costing-rules?status=DRAFT')}
              >
                View All
              </Button>
            }
          >
            <Table
              dataSource={draftRules.filter(rule => rule.proposerId === userData?.id)}
              columns={[
                {
                  title: 'Entity',
                  dataIndex: ['entity', 'name'],
                  key: 'entity',
                  ellipsis: true,
                },
                {
                  title: 'Product',
                  dataIndex: ['product', 'name'],
                  key: 'product',
                  ellipsis: true,
                },
                {
                  title: 'Created',
                  dataIndex: 'createdAt',
                  key: 'createdAt',
                  render: (date) => new Date(date).toLocaleDateString(),
                },
                {
                  title: 'Action',
                  key: 'action',
                  render: (_, record) => (
                    <Button
                      type="link"
                      icon={<EyeOutlined />}
                      onClick={() => handleRuleClick(record.id)}
                    >
                      Edit
                    </Button>
                  ),
                },
              ]}
              pagination={false}
              size="small"
              rowKey="id"
            />
          </Card>
        </Col>

        {/* Rejected Rules */}
        <Col xs={24} lg={12}>
          <Card
            title="Rejected COGS Rules"
            extra={
              <Button
                type="link"
                onClick={() => navigate('/costing-rules?status=REJECTED')}
              >
                View All
              </Button>
            }
          >
            <Table
              dataSource={rejectedRules}
              columns={[
                {
                  title: 'Entity',
                  dataIndex: ['entity', 'name'],
                  key: 'entity',
                  ellipsis: true,
                },
                {
                  title: 'Product',
                  dataIndex: ['product', 'name'],
                  key: 'product',
                  ellipsis: true,
                },
                {
                  title: 'Proposer',
                  dataIndex: ['proposer', 'email'],
                  key: 'proposer',
                  ellipsis: true,
                },
                {
                  title: 'Action',
                  key: 'action',
                  render: (_, record) => (
                    <Button
                      type="link"
                      icon={<EyeOutlined />}
                      onClick={() => handleRuleClick(record.id)}
                    >
                      View
                    </Button>
                  ),
                },
              ]}
              pagination={false}
              size="small"
              rowKey="id"
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
        {/* Quick Actions */}
        <Col xs={24} lg={12}>
          <Card title="Quick Actions">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                block
                onClick={handleCreateCOGS}
              >
                Create New COGS Rule
              </Button>
              <Button
                icon={<FilterOutlined />}
                block
                onClick={() => navigate('/costing-rules')}
              >
                Browse All Rules
              </Button>
              {(userData?.role === 'Admin' || userData?.role === 'Manager') && (
                <Button
                  icon={<CheckCircleOutlined />}
                  block
                  onClick={() => navigate('/approvals')}
                >
                  Review Pending Approvals
                </Button>
              )}
            </Space>
          </Card>
        </Col>

        {/* Admin Management Section - Only for Admins */}
        {userData?.role === 'Admin' && (
          <>
            <Col xs={24}>
              <Card
                title={
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <DatabaseOutlined style={{ marginRight: '8px', color: '#722ed1' }} />
                    <span>System Management</span>
                    <Tag color="red" style={{ marginLeft: '8px' }}>Admin Only</Tag>
                  </div>
                }
                style={{ marginTop: '16px' }}
              >
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12} md={6}>
                    <Card
                      hoverable
                      onClick={() => navigate('/admin/currencies')}
                      style={{ cursor: 'pointer', textAlign: 'center' }}
                      styles={{ body: { padding: '20px' } }}
                    >
                      <DollarOutlined style={{ fontSize: '32px', color: '#52c41a', marginBottom: '8px' }} />
                      <div style={{ fontWeight: 'bold' }}>Currencies</div>
                      <div style={{ color: '#666', fontSize: '12px' }}>Manage ISO 4217 currencies</div>
                    </Card>
                  </Col>

                  <Col xs={24} sm={12} md={6}>
                    <Card
                      hoverable
                      onClick={() => navigate('/admin/pricing-schemes')}
                      style={{ cursor: 'pointer', textAlign: 'center' }}
                      styles={{ body: { padding: '20px' } }}
                    >
                      <AppstoreOutlined style={{ fontSize: '32px', color: '#1890ff', marginBottom: '8px' }} />
                      <div style={{ fontWeight: 'bold' }}>Pricing Schemes</div>
                      <div style={{ color: '#666', fontSize: '12px' }}>Manage pricing models</div>
                    </Card>
                  </Col>

                  <Col xs={24} sm={12} md={6}>
                    <Card
                      hoverable
                      onClick={() => navigate('/admin/products')}
                      style={{ cursor: 'pointer', textAlign: 'center' }}
                      styles={{ body: { padding: '20px' } }}
                    >
                      <AppstoreOutlined style={{ fontSize: '32px', color: '#faad14', marginBottom: '8px' }} />
                      <div style={{ fontWeight: 'bold' }}>Products</div>
                      <div style={{ color: '#666', fontSize: '12px' }}>Manage payment products</div>
                    </Card>
                  </Col>

                  <Col xs={24} sm={12} md={6}>
                    <Card
                      hoverable
                      onClick={() => navigate('/admin/entities')}
                      style={{ cursor: 'pointer', textAlign: 'center' }}
                      styles={{ body: { padding: '20px' } }}
                    >
                      <BankOutlined style={{ fontSize: '32px', color: '#722ed1', marginBottom: '8px' }} />
                      <div style={{ fontWeight: 'bold' }}>Entities</div>
                      <div style={{ color: '#666', fontSize: '12px' }}>Manage business entities</div>
                    </Card>
                  </Col>
                </Row>

                <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
                  <Col xs={24} sm={12}>
                    <Card
                      hoverable
                      onClick={() => navigate('/users')}
                      style={{ cursor: 'pointer', textAlign: 'center' }}
                      styles={{ body: { padding: '20px' } }}
                    >
                      <UserOutlined style={{ fontSize: '32px', color: '#f5222d', marginBottom: '8px' }} />
                      <div style={{ fontWeight: 'bold' }}>User Management</div>
                      <div style={{ color: '#666', fontSize: '12px' }}>Manage system users</div>
                    </Card>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Card
                      hoverable
                      onClick={() => navigate('/admin/user-entity-access')}
                      style={{ cursor: 'pointer', textAlign: 'center' }}
                      styles={{ body: { padding: '20px' } }}
                    >
                      <GlobalOutlined style={{ fontSize: '32px', color: '#13c2c2', marginBottom: '8px' }} />
                      <div style={{ fontWeight: 'bold' }}>Entity Access</div>
                      <div style={{ color: '#666', fontSize: '12px' }}>Manage user permissions</div>
                    </Card>
                  </Col>
                </Row>
              </Card>
            </Col>
          </>
        )}
      </Row>

      {/* Rule Details Modal */}
      <CostingRuleDetails
        ruleId={selectedRuleId}
        visible={detailsVisible}
        onClose={() => {
          setDetailsVisible(false);
          setSelectedRuleId(null);
        }}
        onEdit={(rule) => {
          setDetailsVisible(false);
          const editMode = rule.status === 'APPROVED' ? 'edit-as-new' : 'edit';
          navigate(`/costing-rules/${rule.id}/${editMode}`);
        }}
        refreshData={fetchDashboardData}
      />

      {/* COGS Creation Form Modal */}
      <COGSCreationModal
        visible={createFormVisible}
        onCancel={handleCOGSFormCancel}
        onSuccess={handleCOGSFormSuccess}
        title="Create New COGS Rule"
        width="90%"
        maxWidth="1200px"
      />
    </div>
  );
};

export default HomePage;
